/* eslint-disable no-console */
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { RemoteData, NotAuthorized } from 'rf-comp';
import {
  FormField,
  H1,
  Button,
  ImageUpload, Page
} from 'rf-ui';
import { isAuthorized } from 'rf-lib';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import * as a from './actions';
import { RANDOMNESS_OPTIONS, SPIN_SPEED_OPTIONS } from '../../games/GameFormDetails';

const GroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  padding: 0 0.5rem;
`;

const FormWrapper = styled.div`
  width: 100%;
  max-width: 900px;
  box-sizing: border-box;
  margin: 0 auto;
`;

const DefaultsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  width: 100%;
`;

const DefaultField = styled.div`
  flex: 0 0 220px;
  width: 220px;
`;

const FunSection = styled.div`
  width: 100%;
  margin-top: 0.5rem;
`;

const ImageActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  width: 100%;
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  margin: 0.5rem 0 1rem 0;
`;

const CheckboxInput = styled.input`
  width: 1.4rem;
  height: 1.4rem;
  margin: 0;
`;

const SelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0.5rem 0 1rem 0;
`;

const SelectLabel = styled.label`
  margin-bottom: 0.4rem;
`;

const SelectInput = styled.select`
  border-radius: 3px;
  padding: 0.6rem;
  height: auto;
`;

/* ************************************
***** UPDATE GROUP / DELETE IMAGE *****
************************************ */
const GroupUpdate = ({
  history, authUser, group, groupID, updateGroup, deleteGroupImage
}) => {
  const [error, setError] = useState('');
  const [groupImage, setImage] = useState('');
  const [groupImageURL, setGroupImageURL] = useState(group.imageUrl);

  const toOptionalNumber = value => {
    if (value === '' || value == null) {
      return undefined;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  };

  const toOptionalString = value => {
    if (value == null || value === '') {
      return undefined;
    }
    return value;
  };

  const groupSchema = Yup.object({
    groupName: Yup.string()
      .trim()
      .max(40, 'Gruppenavnet er for langt')
      .min(5, 'Gruppenavnet er for kort, må være mellom 5-40 bokstaver')
      .required('Required'),
    groupDescription: Yup.string()
      .trim()
      .min(5, 'Gruppebeskrivelsen er for kort, må være mellom 5-50 bokstaver')
      .max(50, 'Gruppebeskrivelsen er for lang, må være mellom 5-50 bokstaver')
      .required('Required'),
    paymentInfo: Yup.string()
      .trim()
      .notRequired(),
    defaults: Yup.object({
      ticketPrice: Yup.number().notRequired(),
      maxTickets: Yup.number().notRequired(),
      maxTicketsPerPlayer: Yup.number().notRequired(),
      prizes: Yup.number().notRequired(),
      randomness: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? undefined : value))
        .min(1, 'Randomness må være minst 1')
        .max(5, 'Randomness kan være maks 5')
        .notRequired(),
      spinSpeed: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? undefined : value))
        .min(1, 'Spin speed må være minst 1')
        .max(5, 'Spin speed kan være maks 5')
        .notRequired()
    })
  });

  const handleImageAsFile = file => {
    setImage(file);
  };

  return (
    <GroupContainer>
      <FormWrapper>
        <Formik enableReinitialize
          initialValues={{
            groupName: group.groupName,
            groupDescription: group.groupDescription,
            paymentInfo: group.paymentInfo || '',
            enableAdvanceGameSettings: typeof group.enableAdvanceGameSettings === 'boolean'
              ? group.enableAdvanceGameSettings
              : false,
            defaults: {
              ticketPrice: group.defaults?.ticketPrice ?? '',
              maxTickets: group.defaults?.maxTickets ?? '',
              maxTicketsPerPlayer: group.defaults?.maxTicketsPerPlayer ?? '',
              prizes: group.defaults?.prizes ?? '',
              randomness: group.defaults?.randomness ?? '',
              spinSpeed: group.defaults?.spinSpeed ?? '',
              prohibitNegativeBalance: group.defaults?.prohibitNegativeBalance
            }
          }}
          validationSchema={groupSchema}
          onSubmit={(groupCandidate, { setSubmitting }) => {
            const defaultsCandidate = {
              ticketPrice: toOptionalNumber(groupCandidate.defaults.ticketPrice),
              maxTickets: toOptionalNumber(groupCandidate.defaults.maxTickets),
              maxTicketsPerPlayer: toOptionalNumber(groupCandidate.defaults.maxTicketsPerPlayer),
              prizes: toOptionalNumber(groupCandidate.defaults.prizes),
              randomness: toOptionalNumber(groupCandidate.defaults.randomness),
              spinSpeed: toOptionalNumber(groupCandidate.defaults.spinSpeed),
              prohibitNegativeBalance: groupCandidate.defaults.prohibitNegativeBalance ? true : undefined
            };
            const defaults = Object.fromEntries(
              Object.entries(defaultsCandidate).filter(([, value]) => value !== undefined)
            );
            const groupPayloadCandidate = {
              ...groupCandidate,
              paymentInfo: toOptionalString(groupCandidate.paymentInfo),
              defaults
            };
            const groupPayload = Object.fromEntries(
              Object.entries(groupPayloadCandidate).filter(([, value]) => value !== undefined)
            );

            updateGroup(groupID, groupPayload, groupImage)
              .then(() => history.push('/groups/'))
              .catch(err => setError(err.message));
            setSubmitting(false);
          }}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <FormField
                name="groupName"
                title="Gruppenavn"
              />
              <FormField
                name="groupDescription"
                title="Beskrivelse"
              />
              <FormField
                name="paymentInfo"
                title="Betalingsinformasjon"
              />
              <CheckboxRow htmlFor="enableAdvanceGameSettings">
                <CheckboxInput
                  id="enableAdvanceGameSettings"
                  name="enableAdvanceGameSettings"
                  type="checkbox"
                  checked={Boolean(values.enableAdvanceGameSettings)}
                  onChange={event => setFieldValue('enableAdvanceGameSettings', event.target.checked)}
                />
                Avanserte spenningsnivåer
              </CheckboxRow>
              <h1>Standardverdier for spill</h1>
              <DefaultsContainer>
                <DefaultField>
                  <FormField
                    name="defaults.ticketPrice"
                    title="Pris per lodd"
                    type="number"
                  />
                </DefaultField>
                <DefaultField>
                  <FormField
                    name="defaults.maxTickets"
                    title="Maks lodd"
                    type="number"
                  />
                </DefaultField>
                <DefaultField>
                  <FormField
                    name="defaults.maxTicketsPerPlayer"
                    title="Maks lodd per spiller"
                    type="number"
                  />
                </DefaultField>
                <DefaultField>
                  <FormField
                    name="defaults.prizes"
                    title="Antall premier"
                    type="number"
                  />
                </DefaultField>
                <CheckboxRow htmlFor="defaults.prohibitNegativeBalance">
                  <CheckboxInput
                    id="defaults.prohibitNegativeBalance"
                    name="defaults.prohibitNegativeBalance"
                    type="checkbox"
                    checked={Boolean(values.defaults.prohibitNegativeBalance)}
                    onChange={event => setFieldValue('defaults.prohibitNegativeBalance', event.target.checked)}
                  />
                  Nekt negativ saldo
                </CheckboxRow>
              </DefaultsContainer>
              {values.enableAdvanceGameSettings && (
                <FunSection>
                  <h1>Standardverdier spenningsnivåer</h1>
                  <DefaultsContainer>
                    <DefaultField>
                      <SelectContainer>
                        <SelectLabel htmlFor="defaults.randomness">Spenningsnivå</SelectLabel>
                        <SelectInput
                          id="defaults.randomness"
                          name="defaults.randomness"
                          value={values.defaults.randomness ?? ''}
                          onChange={event => setFieldValue('defaults.randomness', event.target.value)}
                        >
                          <option value="">Velg spenningsnivå</option>
                          {RANDOMNESS_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                              {`${option.value} - ${option.description}`}
                            </option>
                          ))}
                        </SelectInput>
                      </SelectContainer>
                    </DefaultField>
                    <DefaultField>
                      <SelectContainer>
                        <SelectLabel htmlFor="defaults.spinSpeed">Spinnhastighet</SelectLabel>
                        <SelectInput
                          id="defaults.spinSpeed"
                          name="defaults.spinSpeed"
                          value={values.defaults.spinSpeed ?? ''}
                          onChange={event => setFieldValue('defaults.spinSpeed', event.target.value)}
                        >
                          <option value="">Velg spinnhastighet</option>
                          {SPIN_SPEED_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                              {`${option.value} - ${option.description}`}
                            </option>
                          ))}
                        </SelectInput>
                      </SelectContainer>
                    </DefaultField>
                  </DefaultsContainer>
                </FunSection>
              )}
              <ImageActions>
                <ImageUpload title="Velg bilde"
                  url={groupImageURL}
                  onImage={file => handleImageAsFile(file)} />
                <Button onClick={() => deleteGroupImage(groupID).then(setGroupImageURL(''))} title="Slett bilde" />
              </ImageActions>
              <Button title="Lagre" type="submit" />
            </Form>
          )}
        </Formik>
      </FormWrapper>
      {error}
    </GroupContainer>
  );
};

GroupUpdate.propTypes = {
  history: PropTypes.object,
  group: PropTypes.object.isRequired,
  groupID: PropTypes.string.isRequired,
  authUser: PropTypes.object.isRequired,
  updateGroup: PropTypes.func.isRequired,
  deleteGroupImage: PropTypes.func.isRequired,
};

/* *********************
***** DELETE GROUP *****
********************* */
const DeleteGroup = ({
  history, groupID, deleteGroup
}) =>
  <Button onClick={() => deleteGroup(groupID).then((history.push('/groups/')))} title="Slett gruppen" />;

DeleteGroup.propTypes = {
  history: PropTypes.object,
  groupID: PropTypes.string.isRequired,
  deleteGroup: PropTypes.func.isRequired,
};

/* *******************************
***** EDIT PLAYER GROUP MAIN *****
******************************* */
const EditPlayerGroup = ({ authUser, ...props }) => {
  const { id } = useParams();
  const history = useHistory();

  if (!isAuthorized('admin', id, authUser)) {
    return <NotAuthorized message="You are not authorized to edit this player group" />;
  }

  useEffect(() => {
    props.fetchGroupDetails(id);
  }, []);

  return (
    <Page>
      <div>
        <H1>Rediger Gruppe</H1>
        <RemoteData
          type={props.groupDetailsState}
          loading={() => <span>Henter gruppedetaljer...</span>}
          failure={err => <span>Feil: {err.message}</span>}
          success={group => (
            <>
              <GroupUpdate
                history={history}
                authUser={authUser}
                group={group}
                groupID={id}
                updateGroup={props.updateGroup}
                deleteGroupImage={props.deleteGroupImage}
              />
              <DeleteGroup
                history={history}
                groupID={id}
                deleteGroup={props.deleteGroup}
              />
            </>
          )}
        />
        <Button onClick={history.goBack} title="Tilbake" />
      </div>
    </Page>

  );
};

EditPlayerGroup.propTypes = {
  authUser: PropTypes.object,
  groupDetailsState: PropTypes.object.isRequired,
  fetchGroupDetails: PropTypes.func.isRequired,
  updateGroup: PropTypes.func.isRequired,
  deleteGroup: PropTypes.func.isRequired,
  deleteGroupImage: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  fetchGroupDetails: a.getGroupDetails(dispatch),
  updateGroup: a.updateGroup(dispatch),
  deleteGroup: a.deleteGroup(dispatch),
  deleteGroupImage: a.deleteGroupImage(dispatch),
});

const mapStateToProps = ({ session, playerGroupInfo }) => ({
  authUser: session.authUser,
  groupDetailsState: playerGroupInfo.groupDetailsState,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EditPlayerGroup);
